<script setup lang="ts">
/**
 * SpeedDial — вертикальный стек 4 действий, появляется при тапе FAB
 * в BottomNav (inbox-282 / Task 2 mobile-tailwind plan).
 *
 * Backdrop по тапу закрывает шторку. Каждая кнопка открывает соответствующую
 * модалку через useModalStore.
 *
 * Deviation от плана: action «Создать QR» использует modalKey 'qr-customizer'
 * (существующий ModalKey + QrCustomizerModal.vue), а не 'create-qr' из
 * текста задачи — отдельной модалки CreateQrModal в репо нет, добавление
 * новой записи в ModalKey union вне scope этой задачи.
 */

import { LinkIcon, QrCodeIcon, MegaphoneIcon, FileTextIcon } from 'lucide-vue-next';
import { useModalStore, type ModalKey } from '@/stores/modal';

defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: [] }>();

const modals = useModalStore();

type NonNullModalKey = NonNullable<ModalKey>;

interface Action {
  icon: typeof LinkIcon;
  label: string;
  modalKey: NonNullModalKey;
}

const actions: Action[] = [
  { icon: LinkIcon, label: 'Сократить ссылку', modalKey: 'create-link' },
  { icon: QrCodeIcon, label: 'Создать QR', modalKey: 'qr-customizer' },
  { icon: MegaphoneIcon, label: 'Завести кампанию', modalKey: 'create-campaign' },
  { icon: FileTextIcon, label: 'Bio-страница', modalKey: 'create-bio' },
];

function pick(modalKey: NonNullModalKey): void {
  modals.open(modalKey);
  emit('close');
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        class="fixed inset-0 z-50 bg-black/40"
        data-testid="speed-dial-backdrop"
        @click="emit('close')"
      />
    </Transition>

    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-2"
    >
      <ul
        v-if="open"
        class="fixed z-50 bottom-[calc(100px+env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 flex flex-col gap-2 lg:hidden"
        data-testid="speed-dial"
      >
        <li
          v-for="(a, i) in actions"
          :key="a.modalKey"
          :style="{ transitionDelay: `${i * 30}ms` }"
        >
          <button
            type="button"
            class="flex items-center gap-3 min-w-[240px] px-4 py-3 rounded-xl
                   bg-surface-2 border border-border text-fg shadow-lg
                   hover:bg-surface-3 active:bg-surface-3"
            @click="pick(a.modalKey)"
          >
            <component :is="a.icon" class="size-5 text-accent-console" />
            <span class="text-sm font-medium">{{ a.label }}</span>
          </button>
        </li>
      </ul>
    </Transition>
  </Teleport>
</template>
